/**
 * The single door to the Hermes API.
 *
 * Two rules drive the design:
 *  1. The session lives in an HttpOnly cookie, so every call must send
 *     credentials and no token ever touches JS.
 *  2. The home server is frequently unreachable. Upstream failures surface as
 *     typed `ApiError`s (`upstream_unavailable`, `not_configured`) that callers
 *     render as a notice — never as a crash.
 */
import type {
  AccountResponse,
  ActivityEntry,
  AdminUser,
  AdminUserPatch,
  AnnounceInput,
  AnnounceResponse,
  AppNotification,
  DownloadsResponse,
  Invite,
  InviteInput,
  InvitePreview,
  JellyfinLibrary,
  ListResponse,
  LoginResponse,
  MediaRequest,
  MeResponse,
  NotificationPreference,
  NotificationPreferencesResponse,
  Profile,
  ProfileInput,
  ReferralResponse,
  RedeemResponse,
  RequestFilter,
  ServiceHealth,
  SessionSummary,
  User,
  VapidKeyResponse,
} from './types'

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown
  readonly requestId: string | undefined

  constructor(
    status: number,
    code: string,
    message: string,
    options?: { details?: unknown; requestId?: string },
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = options?.details
    this.requestId = options?.requestId
  }
}

/** Thrown when the browser could not reach the Worker at all. */
export const isOfflineError = (error: unknown): boolean =>
  error instanceof ApiError && error.code === 'network_error'

/**
 * True for "the home server is down / not wired up" — an expected state that
 * should render last-known data plus a staleness notice.
 */
export function isUpstreamDown(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false
  if (error.code === 'upstream_unavailable') return true
  if (error.code === 'upstream_error') return true
  if (error.code === 'not_configured') return true
  return error.status === 502 || error.status === 503 || error.status === 504
}

export const isUnauthorized = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401

// ---------------------------------------------------------------------------
// 401 handling
// ---------------------------------------------------------------------------

type UnauthorizedHandler = () => void

let unauthorizedHandler: UnauthorizedHandler | null = null

/**
 * AuthProvider registers here so a 401 anywhere clears auth state and bounces
 * to /login through the router. api.ts stays router-agnostic; the hard redirect
 * below is only a fallback for a 401 that arrives before React mounts.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler): () => void {
  unauthorizedHandler = handler
  return () => {
    if (unauthorizedHandler === handler) unauthorizedHandler = null
  }
}

const PUBLIC_PATH_PREFIXES = ['/login', '/invite/']

function handleUnauthorized(): void {
  if (unauthorizedHandler) {
    unauthorizedHandler()
    return
  }
  const path = window.location.pathname
  if (PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))) return
  window.location.replace('/login')
}

// ---------------------------------------------------------------------------
// Core fetch
// ---------------------------------------------------------------------------

export type QueryValue = string | number | boolean | null | undefined

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, QueryValue>
  signal?: AbortSignal
  /** Set for bootstrap/login calls, where a 401 is an answer, not an eviction. */
  skipAuthRedirect?: boolean
}

function buildUrl(path: string, query?: Record<string, QueryValue>): string {
  const url = `/api${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function parseErrorEnvelope(status: number, payload: unknown): ApiError {
  if (isRecord(payload) && isRecord(payload.error)) {
    const envelope = payload.error
    const code = typeof envelope.code === 'string' ? envelope.code : 'error'
    const message =
      typeof envelope.message === 'string' && envelope.message.trim()
        ? envelope.message
        : `Request failed (${status})`
    return new ApiError(status, code, message, {
      details: envelope.details,
      requestId: typeof envelope.requestId === 'string' ? envelope.requestId : undefined,
    })
  }
  return new ApiError(status, 'error', `Request failed (${status})`)
}

export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, skipAuthRedirect } = options

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  let response: Response
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      // The session cookie is the whole auth story — never omit it.
      credentials: 'include',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ApiError(0, 'network_error', 'Could not reach Hermes. Check your connection.', {
      details: cause,
    })
  }

  let payload: unknown = undefined
  if (response.status !== 204) {
    const text = await response.text()
    if (text) {
      try {
        payload = JSON.parse(text) as unknown
      } catch {
        payload = text
      }
    }
  }

  if (!response.ok) {
    const error = parseErrorEnvelope(response.status, payload)
    if (error.status === 401 && !skipAuthRedirect) handleUnauthorized()
    throw error
  }

  return payload as T
}

// ---------------------------------------------------------------------------
// Status normalisation
// ---------------------------------------------------------------------------

function toServiceHealth(value: unknown): ServiceHealth | null {
  if (!isRecord(value) || typeof value.service !== 'string') return null
  return {
    service: value.service,
    ok: value.ok === true,
    checkedAt: typeof value.checkedAt === 'string' ? value.checkedAt : null,
    latencyMs: typeof value.latencyMs === 'number' ? value.latencyMs : null,
    message: typeof value.message === 'string' ? value.message : null,
  }
}

/**
 * `/api/status` is the one endpoint that must never fail to render — it is what
 * tells the user the rest of the app is degraded. Accept the documented array
 * as well as the `{ items }` / `{ services }` wrappers rather than blowing up.
 */
export function normalizeStatus(payload: unknown): ServiceHealth[] {
  const raw: unknown = Array.isArray(payload)
    ? payload
    : isRecord(payload)
      ? Array.isArray(payload.services)
        ? payload.services
        : Array.isArray(payload.items)
          ? payload.items
          : []
      : []

  if (!Array.isArray(raw)) return []
  const out: ServiceHealth[] = []
  for (const entry of raw) {
    const health = toServiceHealth(entry)
    if (health) out.push(health)
  }
  return out
}

// ---------------------------------------------------------------------------
// Per-resource helpers
// ---------------------------------------------------------------------------

export const api = {
  auth: {
    me: (signal?: AbortSignal) =>
      apiFetch<MeResponse>('/auth/me', { signal, skipAuthRedirect: true }),
    login: (input: { username: string; password: string }) =>
      apiFetch<LoginResponse>('/auth/login', {
        method: 'POST',
        body: input,
        skipAuthRedirect: true,
      }),
    logout: () => apiFetch<void>('/auth/logout', { method: 'POST' }),
    sessions: (signal?: AbortSignal) =>
      apiFetch<ListResponse<SessionSummary>>('/auth/sessions', { signal }),
    revokeSession: (id: string) =>
      apiFetch<void>(`/auth/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },

  account: {
    get: (signal?: AbortSignal) => apiFetch<AccountResponse>('/account', { signal }),
    update: (input: { email?: string | null }) =>
      apiFetch<AccountResponse>('/account', { method: 'PATCH', body: input }),
    changePassword: (input: { currentPassword: string; newPassword: string }) =>
      apiFetch<void>('/account/password', { method: 'POST', body: input }),

    referral: (signal?: AbortSignal) =>
      apiFetch<ReferralResponse>('/account/referral', { signal }),

    /** Diagnostics: how this client is running. Fire-and-forget. */
    reportClient: (input: {
      standalone: boolean
      platform: 'ios' | 'android' | 'desktop'
      pushPermission: 'granted' | 'denied' | 'default' | 'unsupported'
    }) => apiFetch<{ ok: boolean }>('/account/client', { method: 'POST', body: input }),
  },

  requests: {
    list: (params: { status?: RequestFilter; userId?: string } = {}, signal?: AbortSignal) =>
      apiFetch<ListResponse<MediaRequest>>('/requests', {
        query: { status: params.status, userId: params.userId },
        signal,
      }),
    get: (id: number, signal?: AbortSignal) =>
      apiFetch<MediaRequest>(`/requests/${id}`, { signal }),
  },

  downloads: {
    list: (signal?: AbortSignal) => apiFetch<DownloadsResponse>('/downloads', { signal }),
  },

  notifications: {
    list: (params: { unreadOnly?: boolean; limit?: number } = {}, signal?: AbortSignal) =>
      apiFetch<ListResponse<AppNotification>>('/notifications', {
        query: { unreadOnly: params.unreadOnly ? 'true' : undefined, limit: params.limit },
        signal,
      }),
    markRead: (ids?: string[]) =>
      apiFetch<void>('/notifications/read', { method: 'POST', body: ids ? { ids } : {} }),
    preferences: (signal?: AbortSignal) =>
      apiFetch<NotificationPreferencesResponse>('/notifications/preferences', { signal }),
    savePreferences: (preferences: NotificationPreference[]) =>
      apiFetch<NotificationPreferencesResponse>('/notifications/preferences', {
        method: 'PUT',
        body: { preferences },
      }),
  },

  push: {
    vapidPublicKey: (signal?: AbortSignal) =>
      apiFetch<VapidKeyResponse>('/push/vapid-public-key', { signal }),
    subscribe: (subscription: PushSubscriptionJSON) =>
      apiFetch<void>('/push/subscribe', { method: 'POST', body: subscription }),
    unsubscribe: (endpoint: string) =>
      apiFetch<void>('/push/unsubscribe', { method: 'POST', body: { endpoint } }),
    test: () => apiFetch<void>('/push/test', { method: 'POST' }),
  },

  invites: {
    preview: (code: string, signal?: AbortSignal) =>
      apiFetch<InvitePreview>(`/invites/${encodeURIComponent(code)}/preview`, {
        signal,
        skipAuthRedirect: true,
      }),
    redeem: (code: string, input: { username: string; password: string; email?: string }) =>
      apiFetch<RedeemResponse>(`/invites/${encodeURIComponent(code)}/redeem`, {
        method: 'POST',
        body: input,
        skipAuthRedirect: true,
      }),
  },

  status: {
    get: async (signal?: AbortSignal): Promise<ServiceHealth[]> =>
      normalizeStatus(await apiFetch<unknown>('/status', { signal, skipAuthRedirect: true })),
  },

  admin: {
    users: (signal?: AbortSignal) => apiFetch<ListResponse<AdminUser>>('/admin/users', { signal }),
    updateUser: (id: string, patch: AdminUserPatch) =>
      apiFetch<AdminUser>(`/admin/users/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: patch,
      }),
    deleteUser: (id: string) =>
      apiFetch<void>(`/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    approveUser: (id: string) =>
      apiFetch<{ user: AdminUser }>(`/admin/users/${id}/approve`, { method: 'POST' }),
    rejectUser: (id: string) =>
      apiFetch<{ user: AdminUser }>(`/admin/users/${id}/reject`, { method: 'POST' }),

    applyProfile: (id: string) =>
      apiFetch<User>(`/admin/users/${encodeURIComponent(id)}/apply-profile`, { method: 'POST' }),

    invites: (signal?: AbortSignal) => apiFetch<ListResponse<Invite>>('/admin/invites', { signal }),
    createInvite: (input: InviteInput) =>
      apiFetch<Invite>('/admin/invites', { method: 'POST', body: input }),
    revokeInvite: (id: string) =>
      apiFetch<void>(`/admin/invites/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    profiles: (signal?: AbortSignal) =>
      apiFetch<ListResponse<Profile>>('/admin/profiles', { signal }),
    createProfile: (input: ProfileInput) =>
      apiFetch<Profile>('/admin/profiles', { method: 'POST', body: input }),
    updateProfile: (id: string, input: Partial<ProfileInput>) =>
      apiFetch<Profile>(`/admin/profiles/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: input,
      }),
    deleteProfile: (id: string) =>
      apiFetch<void>(`/admin/profiles/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    libraries: (signal?: AbortSignal) =>
      apiFetch<ListResponse<JellyfinLibrary>>('/admin/jellyfin/libraries', { signal }),

    activity: (params: { limit?: number; offset?: number } = {}, signal?: AbortSignal) =>
      apiFetch<ListResponse<ActivityEntry>>('/admin/activity', {
        query: { limit: params.limit, offset: params.offset },
        signal,
      }),

    announce: (input: AnnounceInput) =>
      apiFetch<AnnounceResponse>('/admin/announce', { method: 'POST', body: input }),
  },
} as const
